// The Buyers workspace (record-page redesign, build step 5): the seller deal's
// board and outreach tracker on one state at
//   #/deals/<id>?panel=buyers&tab=board|outreach[&status=…][&focus=<buyer engagement id>]
// Registered in views/record_panels.jsx (v2 desktop/iPad only). PanelHost
// mounts it on first open and keeps it, hidden (`active` false), while the
// deal is open. window.BuyersKit holds the rules the Buyers cards share
// (buyer_board.jsx); atlas-crm/test/buyers_workspace.test.mjs runs it.

// ── BuyersKit (pure) ─────────────────────────────────────────────────────
// Status predicates = the outreach tracker's KPIs (app/outreach.kpis) and the
// summary's buyer_counts (routes/record_summary.py).
const BW_STATUS = Object.freeze({
  live:   (b) => b.interest_level !== 'passed',
  passed: (b) => b.interest_level === 'passed',
  nda:    (b) => b.nda_status === 'signed',
  cim:    (b) => !!b.cim_sent,
  ioi:    (b) => !!b.ioi_date,
  loi:    (b) => !!b.loi_submitted,
});
// URL status ↔ summary count key, in chip order (the card's chips).
const BW_CHIPS = Object.freeze([
  { status: 'live', key: 'live', label: 'Live' },
  { status: 'nda', key: 'nda_signed', label: 'NDA signed' },
  { status: 'cim', key: 'cim', label: 'CIM' },
  { status: 'ioi', key: 'ioi', label: 'IOI' },
  { status: 'loi', key: 'loi', label: 'LOI' },
  { status: 'passed', key: 'passed', label: 'Passed' },
].map(Object.freeze));
const bwStatusOf = (s) => (typeof s === 'string' && Object.prototype.hasOwnProperty.call(BW_STATUS, s) ? s : 'all');
const bwCounts = (rows) => {
  const r = rows || [];
  const n = (k) => r.filter(BW_STATUS[k]).length;
  return { total: r.length, live: n('live'), passed: n('passed'), nda_signed: n('nda'), cim: n('cim'), ioi: n('ioi'), loi: n('loi') };
};
const bwPlural = (n) => `${n} buyer${n === 1 ? '' : 's'}`;
// One row's patch for a bulk action; null = nothing to change on this row
// (a real historical date is never overwritten).
const bwBulkPatch = (action, opts, row) => {
  const o = opts || {}; const b = row || {};
  if (action === 'nda') {
    if (!o.date) return null;
    if (b.nda_status === 'signed') return b.nda_signed_date ? null : { nda_signed_date: o.date };
    return { nda_status: 'signed', nda_signed_date: o.date };
  }
  if (action === 'cim') {
    if (!o.date) return null;
    if (b.cim_sent) return b.cim_sent_date ? null : { cim_sent_date: o.date };
    return { cim_sent: true, cim_sent_date: o.date };
  }
  if (action === 'pass') {
    const reason = o.reason || null;
    if (!reason) return null;
    const detail = reason === 'other' ? (String(o.detail || '').trim() || null) : null;
    if (b.interest_level === 'passed' && b.pass_reason === reason
      && (reason !== 'other' || (b.pass_detail || null) === detail)) return null;
    const p = { interest_level: 'passed', pass_reason: reason };
    if (reason === 'other') p.pass_detail = detail;
    else if (b.pass_reason === 'other') p.pass_detail = null;     // leaving "other" drops its text (PassReasonCell)
    return p;
  }
  return null;
};
const bwBulkTitle = (action, n) => (action === 'pass' ? `Pass ${bwPlural(n)}?`
  : action === 'nda' ? `Mark ${bwPlural(n)} NDA signed?`
  : action === 'cim' ? `Mark the CIM sent to ${bwPlural(n)}?` : `Update ${bwPlural(n)}?`);
const bwBulkDone = (action, ok, n) => (action === 'pass' ? `Passed ${ok} of ${n}`
  : action === 'nda' ? `NDA signed on ${ok} of ${n}` : action === 'cim' ? `CIM sent to ${ok} of ${n}` : `Updated ${ok} of ${n}`);
// The confirm's body and button: counts and the one consequence, no narration.
const bwBulkBody = (action, skipped) => {
  const parts = [action === 'pass' ? 'Pursuits → Lost' : null, skipped ? `${skipped} skipped` : null].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
};
const bwBulkConfirmLabel = (action) => (action === 'pass' ? 'Pass' : action === 'nda' ? 'Mark signed' : action === 'cim' ? 'Mark sent' : 'Update');
// ── The workspace board filter: #700's two axes (status × NDA signed). The
// URL `status` is single-valued; `nda` there means All ∧ NDA signed (the
// card's deep link). Any other axis carries the NDA toggle in memory.
const BW_BOARD_CHIPS = Object.freeze([
  { status: 'live', label: 'Live' }, { status: 'cim', label: 'CIM' }, { status: 'ioi', label: 'IOI' },
  { status: 'loi', label: 'LOI' }, { status: 'passed', label: 'Passed' },
].map(Object.freeze));
const bwBoardFilter = (status, ndaOnly) => {
  const st = bwStatusOf(status);
  return { status: st, axis: st === 'nda' ? 'all' : st, nda: st === 'nda' || !!ndaOnly };
};
const bwBoardMatch = (b, f) => (f.axis === 'all' || BW_STATUS[f.axis](b)) && (!f.nda || BW_STATUS.nda(b));
// Each chip counts what clicking it would show, given the other filter
// (classic BuyerEngagementsSection's rule).
const bwBoardCounts = (rows, f) => {
  const r = rows || [];
  const pool = f.nda ? r.filter(BW_STATUS.nda) : r;
  const out = { all: pool.length };
  BW_BOARD_CHIPS.forEach((c) => { out[c.status] = pool.filter(BW_STATUS[c.status]).length; });
  out.nda = (f.axis === 'all' ? r : r.filter(BW_STATUS[f.axis])).filter(BW_STATUS.nda).length;
  return out;
};
// What a click does: { axis: k } picks a status chip (k = 'all' too); { nda: true }
// flips the NDA toggle. Returns the URL status (null = absent) and the memory flag.
const bwBoardNext = (f, click) => {
  if (click && click.nda) {
    const on = !f.nda;
    return f.axis === 'all' ? { status: on ? 'nda' : null, ndaOnly: false } : { status: f.axis, ndaOnly: on };
  }
  const k = bwStatusOf(click && click.axis);
  if (k === 'nda') return { status: 'nda', ndaOnly: false };              // not a row chip; the deep-link value
  if (k === 'all') return { status: f.nda ? 'nda' : null, ndaOnly: false };
  return { status: k, ndaOnly: !!f.nda };
};
// Rows grouped per deal in `order` (deal ids; unknown ones follow in first-seen
// order), live first, `cap` rows each (the company Buyers card: 10).
const bwGroupByDeal = (rows, cap, order) => {
  const by = new Map();
  (order || []).forEach((id) => by.set(String(id), []));
  (rows || []).forEach((b) => { const k = String(b.engagement_id); if (!by.has(k)) by.set(k, []); by.get(k).push(b); });
  const out = [];
  by.forEach((list, engagementId) => {
    if (!list.length) return;
    const sorted = list.filter(BW_STATUS.live).concat(list.filter(BW_STATUS.passed));
    const shown = cap > 0 ? sorted.slice(0, cap) : sorted;
    const c = bwCounts(list);
    out.push({ engagementId, rows: shown, total: c.total, live: c.live, nda: c.nda_signed, passed: c.passed });
  });
  return out;
};
const bwOutreachLine = (k) => {
  const x = k || {};
  return `Emailed ${x.emailed || 0} · Replied ${x.replied || 0} · Waiting on us ${x.waiting_on_us || 0}`;
};
const bwDeadlineChip = (nd) => (nd && nd.label ? `${nd.label} due ${nd.days === 0 ? 'today' : `${nd.days}d`}` : null);
// The classic board's wording (buyer_board.jsx searchSummary), off the summary's buyer_search.
const bwSearchSummary = (bs) => {
  if (!bs) return null;
  const when = bs.finished_at ? new Date(bs.finished_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : null;
  return `${bs.recommended != null ? `${bs.recommended} recommended of ` : ''}${bs.judged || 0} judged${when ? ` · ${when}` : ''}`;
};
const bwCardPreview = (b) => (b ? `${b.live || 0} live · ${b.nda_signed || 0} NDA · ${b.passed || 0} passed` : '—');
window.BuyersKit = Object.freeze({
  STATUS: BW_STATUS, CHIPS: BW_CHIPS, BOARD_CHIPS: BW_BOARD_CHIPS, statusOf: bwStatusOf, counts: bwCounts,
  boardFilter: bwBoardFilter, boardMatch: bwBoardMatch, boardCounts: bwBoardCounts, boardNext: bwBoardNext,
  bulkPatch: bwBulkPatch, bulkTitle: bwBulkTitle, bulkBody: bwBulkBody, bulkConfirmLabel: bwBulkConfirmLabel,
  bulkDone: bwBulkDone, groupByDeal: bwGroupByDeal, outreachLine: bwOutreachLine,
  deadlineChip: bwDeadlineChip, searchSummary: bwSearchSummary, cardPreview: bwCardPreview,
});

const BW_EVT = () => (window.RECORD_SUMMARY && window.RECORD_SUMMARY.EVT_BUYERS) || 'atlas-buyers-changed';

// ── The deal's board: loaded on first open and on every reopen; another
// surface's write (the bus) reloads it while shown. `loads` counts the loads
// that landed (a reopen's ?focus= waits for one that started after it).
const useDealBuyers = (dealId, opts) => {
  const active = !opts || opts.active !== false;
  const own = (opts && opts.source) || null;
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loads, setLoads] = React.useState(0);
  const rowsRef = React.useRef(rows); rowsRef.current = rows;
  const activeRef = React.useRef(active); activeRef.current = active;
  const seq = React.useRef(0);
  const reload = React.useCallback(() => {
    if (!dealId) return Promise.resolve();
    const n = ++seq.current;
    return window.AtlasApi.buyersForEngagement(dealId)
      .then((r) => { if (n === seq.current) { setRows((r && r.items) || []); setError(null); setLoads((x) => x + 1); } })
      .catch((e) => { if (n === seq.current) setError((e && e.message) || 'error'); });
  }, [dealId]);
  const was = React.useRef(false);
  React.useEffect(() => {                           // first open + every false → true
    if (active && !was.current) reload();
    was.current = active;
  }, [active, reload]);
  React.useEffect(() => {
    const evt = BW_EVT();
    const on = (e) => {
      const d = (e && e.detail) || {};
      if (!activeRef.current || String(d.engagementId) !== String(dealId) || (own && d.source === own)) return;
      reload();
    };
    window.addEventListener(evt, on);
    return () => window.removeEventListener(evt, on);
  }, [dealId, own, reload]);
  const merge = React.useCallback((beId, row) => {
    if (!row) return;
    setRows((prev) => (prev || []).map((b) => (b.id === beId ? { ...b, ...row } : b)));
  }, []);
  // The deal page's per-row path (DealDetail patchBuyerEng), plus a toast on failure and the bus.
  // The rethrown error carries `toasted`, so a cell with its own toast (PassReasonCell) stays quiet.
  const patch = React.useCallback(async (beId, p) => {
    const prev = (rowsRef.current || []).find((b) => b.id === beId);
    let updated;
    try { updated = await window.AtlasApi.updateBuyerEngagement(beId, p); }
    catch (e) {
      window.pushToast({ type: 'error', title: 'Save failed', body: e && e.message });
      if (e && typeof e === 'object') e.toasted = true;
      throw e;
    }
    merge(beId, updated);
    if (prev && updated && updated.pursuit_stage && updated.pursuit_stage !== prev.pursuit_stage) {
      window.pushToast({ type: 'success', title: `Pursuit moved to ${window.cleanDcStage(updated.pursuit_stage)}`,
        body: `${prev.buyer_name || 'The buyer'}'s pursuit record follows — DealCloud too.` });
    }
    if (window.notifyBuyersChanged) window.notifyBuyersChanged(dealId, own);
    return updated;
  }, [dealId, own, merge]);
  return { rows, error, loads, reload, patch, merge };
};
window.useDealBuyers = useDealBuyers;

const BW_DIM = { color: 'var(--text-4)' };

// The deal's stage: a requestStage picker for writers on a live deal
// (Won → ClosingDrawer, already z70 while a workspace is open), else a pill.
const BuyersStagePill = ({ deal, ctx, canWrite }) => {
  const [picking, setPicking] = React.useState(false);
  const list = (ctx && ctx.stageList) || [];
  const idx = ctx && Number.isInteger(ctx.currentIdx) ? ctx.currentIdx : -1;
  const hue = (window.STAGE_DOT_HUE || {})[deal.stage] || 'slate';
  const pill = <window.Pill hue={hue} dot>{deal.stageName || deal.stage || '—'}</window.Pill>;
  const can = canWrite && !deal.exitName && list.length > 0 && !!ctx && typeof ctx.requestStage === 'function';
  if (!can) return <span data-buyers-stage="" style={{ flexShrink: 0 }}>{pill}</span>;
  if (picking) {
    return (
      <window.Combobox autoOpen dense value={idx >= 0 ? list[idx].id : ''} menuMinWidth={220} style={{ width: 200 }}
        options={list.map((st) => ({ value: st.id, label: st.name }))}
        onClose={() => setPicking(false)}
        onChange={(e) => {
          setPicking(false);
          const i = list.findIndex((st) => st.id === e.target.value);
          if (i >= 0 && i !== idx) ctx.requestStage(list[i], i);
        }} />
    );
  }
  return (
    <button type="button" data-buyers-stage="" onClick={() => setPicking(true)} title="Change stage"
      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }}>{pill}</button>
  );
};

// KPIs, the process line and the buyer search, off the record summary (the
// card's entry). Not rendered for guests.
const BuyersSubHeader = ({ deal, sum, canWrite, onAdd, onSearch }) => {
  const d = sum.data || {};
  const o = d.outreach || null;
  const bs = d.buyer_search || null;
  const done = !!bs && !bs.running && bs.status === 'done';
  return (
    <div data-buyers-subheader="" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
      padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
      {o && o.kpis && window.OutreachKpis ? <window.OutreachKpis k={o.kpis} compact /> : <span style={BW_DIM}>—</span>}
      {o && window.OutreachProcessLine && (
        <window.OutreachProcessLine deal={deal} process={o.process} readOnly={!canWrite} onSaved={() => sum.refresh()} />
      )}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'var(--text-3)' }}>
        {bs && bs.running && !canWrite && <window.Pill hue="blue" sz="sm">Search running…</window.Pill>}
        {done && <span>Buyer search: {window.BuyersKit.searchSummary(bs)}</span>}
        {canWrite && window.BuyerSearchDrawer && window.FindBuyersButton && (
          <window.FindBuyersButton deal={deal} search={bs} onStarted={() => sum.refresh()} onOpen={onSearch} />
        )}
      </span>
      <span style={{ flex: 1 }} />
      {canWrite && window.AddBuyerToDealDrawer && (
        <window.Btn size="xs" variant="outline" icon={<window.Icon.Plus size={10} />} onClick={onAdd}>Add buyer</window.Btn>
      )}
    </div>
  );
};

// `{n} selected · NDA signed… · CIM sent · Pass… · Clear`; NDA asks one date,
// Pass one reason (+ the "other" text). The caller confirms and runs it;
// `onRun` resolves true when it ran (or had nothing to do), false when the
// confirm was cancelled, which keeps the form open. While a run is going:
// `{done} / {total}` and Stop. `active`: the workspace shows the Board tab.
const BuyersBulkBar = ({ n, active, progress, onRun, onClear, onStop }) => {
  const [mode, setMode] = React.useState(null);                 // null | 'nda' | 'pass'
  const [date, setDate] = React.useState(() => window.localISODate());
  const [reason, setReason] = React.useState('');
  const [detail, setDetail] = React.useState('');
  const onScreen = !!active && (!!n || !!progress);
  // Off screen (selection cleared, another tab, the workspace closed): the form
  // closes and clears, so it never holds an Escape entry nobody can see.
  React.useEffect(() => {
    if (!onScreen && mode) { setMode(null); setReason(''); setDetail(''); }
  }, [onScreen, mode]);
  // Escape cancels the form while it shows, before the workspace.
  window.useEscapeKey(onScreen && !progress && !!mode, () => setMode(null));
  if (!n && !progress) return null;
  const busy = !!progress;
  // A run (or nothing to do) closes the form and clears it; a cancelled confirm keeps both.
  const run = (action, opts) => Promise.resolve(onRun(action, opts)).then((did) => {
    if (did) { setMode(null); setReason(''); setDetail(''); }
  });
  const input = { padding: '3px 6px', fontSize: 12, fontFamily: 'inherit', border: '1px solid var(--border)', borderRadius: 6,
    background: 'var(--surface)', color: 'var(--text)' };
  return (
    <span data-buyers-bulk="" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
      <span className="num" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)' }}>
        {busy ? `${progress.done} / ${progress.total}` : `${n} selected`}
      </span>
      {busy && <window.Btn size="xs" variant="ghost" onClick={onStop}>Stop</window.Btn>}
      {!busy && mode === null && (
        <>
          <window.Btn size="xs" variant="outline" onClick={() => { setDate(window.localISODate()); setMode('nda'); }}>NDA signed…</window.Btn>
          <window.Btn size="xs" variant="outline" onClick={() => run('cim', { date: window.localISODate() })}>CIM sent</window.Btn>
          <window.Btn size="xs" variant="outline" onClick={() => setMode('pass')}>Pass…</window.Btn>
          <window.Btn size="xs" variant="ghost" onClick={onClear}>Clear</window.Btn>
        </>
      )}
      {!busy && mode === 'nda' && (
        <>
          <input type="date" aria-label="NDA signed on" value={date} onChange={(e) => setDate(e.target.value)} style={input} />
          <window.Btn size="xs" variant="accent" disabled={!date} onClick={() => run('nda', { date })}>Apply</window.Btn>
          <window.Btn size="xs" variant="ghost" onClick={() => setMode(null)}>Cancel</window.Btn>
        </>
      )}
      {!busy && mode === 'pass' && (
        <>
          <window.Combobox dense value={reason} options={window.BE_PASS_REASONS || []} placeholder="Reason" menuMinWidth={170}
            style={{ width: 170 }} onChange={(e) => setReason(e.target.value)} />
          {reason === 'other' && (
            <input value={detail} maxLength={200} placeholder="Why did they pass?" aria-label="Pass reason"
              onChange={(e) => setDetail(e.target.value)} style={{ ...input, width: 200 }} />
          )}
          <window.Btn size="xs" variant="accent" disabled={!reason} onClick={() => run('pass', { reason, detail })}>Apply</window.Btn>
          <window.Btn size="xs" variant="ghost" onClick={() => setMode(null)}>Cancel</window.Btn>
        </>
      )}
    </span>
  );
};

// The workspace's composer drawer (Log ▾, step 10): modeless, portaled into the
// workspace panel by RecordDrawer, and kept below the panel's header (`top`) so
// the header's × / Log ▾ / stage pill stay reachable while the draft is open.
// Opened, it focuses the draft's field as the page's pane does (RecordComposer's
// data-composer-focus; the field mounts a commit after the drawer, hence the retries).
const WORKSPACE_COMPOSER_FIELD = 'textarea, input:not([type="hidden"]):not([type="checkbox"]):not([type="file"])';
const WorkspaceComposerDrawer = ({ open, onClose, children }) => {
  const layer = React.useContext(window.LayerContext);
  const boxRef = React.useRef(null);
  const [top, setTop] = React.useState(0);
  React.useLayoutEffect(() => {
    if (!open) return;
    const hdr = layer.portalTarget ? layer.portalTarget.querySelector(':scope > header') : null;
    setTop(hdr ? hdr.offsetHeight : 0);
  }, [open, layer.portalTarget]);
  React.useEffect(() => {
    if (!open) return undefined;
    let tries = 5; let raf = 0;
    const focus = () => {
      const el = boxRef.current;
      const box = el && el.querySelector('[data-composer-focus]');
      let f = null;
      if (box) { try { f = box.querySelector(box.getAttribute('data-composer-focus')); } catch (_) { f = null; } }
      if (!f && el) f = el.querySelector(WORKSPACE_COMPOSER_FIELD);
      if (f) { if (!el.contains(document.activeElement)) { try { f.focus({ preventScroll: true }); } catch (_) { /* detached */ } } return; }
      if (tries-- > 0) raf = requestAnimationFrame(focus);
    };
    raf = requestAnimationFrame(focus);
    return () => cancelAnimationFrame(raf);
  }, [open]);
  return (
    <window.RecordDrawer name="composer" modeless open={open} title="Log" width={560} top={top} onClose={onClose}>
      <div ref={boxRef}>{children}</div>
    </window.RecordDrawer>
  );
};

const BuyersWorkspace = ({ api }) => {
  const deal = api.record || {};
  const dealId = api.rec.id;
  const guest = !!api.guest;
  const canWrite = !!api.canWrite && !guest;
  const params = api.params || {};
  const tab = guest ? 'board' : (params.tab === 'outreach' ? 'outreach' : 'board');
  // #700's two axes: the URL status (All ∧ NDA = status=nda) × the NDA toggle,
  // which lives here in memory while another status is picked.
  const [ndaOnly, setNdaOnly] = React.useState(false);
  const filter = window.BuyersKit.boardFilter(params.status, ndaOnly);
  const own = React.useId();                                    // this workspace's bus token
  const sum = window.useRecordSummary('deal', dealId);          // the card's entry: no second request
  const buyers = useDealBuyers(dealId, { active: api.active, source: own });
  const [trackerWanted, setTrackerWanted] = React.useState(false);
  const wantTracker = !guest && (trackerWanted || (api.active && tab === 'outreach'));
  const t = window.useOutreach(wantTracker ? dealId : null);    // always called; lazy until the Outreach tab shows
  const [selected, setSelected] = React.useState(() => new Set());
  const [progress, setProgress] = React.useState(null);         // { done, total } while a bulk runs
  const [flash, setFlash] = React.useState(null);
  const [adding, setAdding] = React.useState(false);
  const [searching, setSearching] = React.useState(false);
  const boardRef = React.useRef(null);
  const focusDone = React.useRef(null);
  const openLoads = React.useRef(0);                            // buyers.loads when this open began
  const trackerStale = React.useRef(false);
  const stopRef = React.useRef(false);                          // Stop or unmount: the bulk loop checks it before each row
  const holdRef = React.useRef(null);                           // the running bulk's summary-hold release (once-only)
  const live = React.useRef({}); live.current = { active: api.active, tab, wantTracker, reload: t.reload };
  // Log ▾ inside the workspace (step 10): the record's one draft moves into a
  // modeless drawer here (RecordComposerHost 'workspace'); closing the workspace
  // hands it back to the page's pane.
  const host = window.useComposerHost('deal', dealId);
  const draftOpen = window.useRecordDraftOpen('deal', dealId);
  const draftDot = window.useRecordDraftFlag('deal', dealId);

  // The first want is useOutreach's own first fetch: nothing is stale yet.
  React.useEffect(() => {
    if (wantTracker && !trackerWanted) { trackerStale.current = false; setTrackerWanted(true); }
  }, [wantTracker, trackerWanted]);
  // A guest has no Outreach tab: fix the URL in place (replace), never strip the workspace.
  React.useEffect(() => {
    if (api.active && guest && params.tab === 'outreach') api.set({ tab: 'board' });
  }, [api.active, guest, params.tab]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Reopened: anything may have changed; the board reloads itself (useDealBuyers).
  // Closed: its drawers close (BuyerSearchDrawer polls while a search runs, and a
  // hidden workspace only hides them; the tracker closes its own on `active`),
  // the selection and the NDA toggle reset.
  const wasActive = React.useRef(api.active);
  React.useEffect(() => {
    if (api.active && !wasActive.current) { trackerStale.current = true; openLoads.current = buyers.loads; }
    if (!api.active && wasActive.current) {
      setAdding(false); setSearching(false); setSelected(new Set()); setNdaOnly(false);
      focusDone.current = null;                                 // the same row can be focused again next time
    }
    wasActive.current = api.active;
  }, [api.active]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!api.active && window.RecordComposerHost) window.RecordComposerHost.claim('deal', dealId, 'pane');
  }, [api.active, dealId]);
  // Unmounted with the claim held (Back, another record while the workspace is open —
  // PanelHost remounts workspaces per record page, so the inactive effect never ran):
  // the pane gets the draft back. A StrictMode double-mount runs this
  // cleanup once on a 'pane' host: a no-op (claim returns early when unchanged).
  React.useEffect(() => () => { if (window.RecordComposerHost) window.RecordComposerHost.claim('deal', dealId, 'pane'); }, [dealId]);
  // Leaving the deal mid-run stops the bulk loop and lets the summary go. (A
  // StrictMode remount runs this cleanup too: runBulk resets stopRef when it starts.)
  React.useEffect(() => () => {
    stopRef.current = true;
    if (holdRef.current) holdRef.current();
  }, []);
  // A stale tracker reloads when its tab is on screen.
  React.useEffect(() => {
    if (!api.active || tab !== 'outreach' || !trackerWanted || !trackerStale.current) return;
    trackerStale.current = false;
    t.reload();
  }, [api.active, tab, trackerWanted]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Another surface's buyer write (card, Market "Add to deal", …) stales the tracker.
  React.useEffect(() => {
    const evt = BW_EVT();
    const on = (e) => {
      const d = (e && e.detail) || {};
      if (String(d.engagementId) !== String(dealId) || d.source === own) return;
      trackerStale.current = true;
      const l = live.current;
      if (l.active && l.tab === 'outreach' && l.wantTracker) { trackerStale.current = false; l.reload(); }
    };
    window.addEventListener(evt, on);
    return () => window.removeEventListener(evt, on);
  }, [dealId, own]);
  // A filter change clears the selection: bulk only ever touches rows on screen.
  React.useEffect(() => { setSelected(new Set()); }, [filter.status, ndaOnly]);
  // ?focus=<buyer engagement id>: unfilter if hidden, scroll it in, highlight once.
  // A row missing from the board waits for a load that started with this open
  // (a buyer added while it was hidden); still missing then = ignored.
  React.useEffect(() => {
    const id = params.focus;
    if (!api.active || tab !== 'board' || !id || !buyers.rows || focusDone.current === id) return;
    const row = buyers.rows.find((b) => b.id === id);
    if (!row) { if (buyers.loads > openLoads.current) focusDone.current = id; return; }
    if (!window.BuyersKit.boardMatch(row, filter)) {               // hidden by a filter: show all, this effect re-runs
      setNdaOnly(false);
      if (filter.status !== 'all') api.set({ status: null });
      return;
    }
    focusDone.current = id;
    const pane = boardRef.current;
    const el = pane && Array.from(pane.querySelectorAll('[data-be-row]')).find((x) => x.getAttribute('data-be-row') === id);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' });
    setFlash(id);
  }, [api.active, tab, params.focus, buyers.rows, buyers.loads, filter.status, ndaOnly]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!flash) return undefined;
    const tm = setTimeout(() => setFlash(null), 2400);
    return () => clearTimeout(tm);
  }, [flash]);

  // ── Done with hooks. Safe to branch below.

  if (deal.type === 'buyer_buyside') return null;               // the registry already strips shells
  const kit = window.BuyersKit;
  const rows = buyers.rows;
  const shown = rows ? rows.filter((b) => kit.boardMatch(b, filter)) : [];
  const counts = kit.boardCounts(rows || [], filter);
  const picked = shown.filter((b) => selected.has(b.id));
  const o = (sum.data && sum.data.outreach) || null;
  const notify = () => { if (window.notifyBuyersChanged) window.notifyBuyersChanged(dealId, own); };
  const boardWrote = () => { trackerStale.current = true; };
  const boardPatch = (id, p) => buyers.patch(id, p).then((r) => { boardWrote(); return r; });
  const onRowUpdate = (id, row) => { buyers.merge(id, row); boardWrote(); notify(); };
  // Every reload OutreachTracker asks for follows a write: tell the bus, refresh
  // the board. A load-error Retry (`retry`) only reloads the tracker.
  const trackerProp = { data: t.data, error: t.error, retry: t.reload,
    reload: () => { notify(); buyers.reload(); return t.reload(); } };
  const select = canWrite ? {
    ids: selected, disabled: !!progress,
    toggle: (id) => setSelected((s) => { const x = new Set(s); if (x.has(id)) x.delete(id); else x.add(id); return x; }),
    setAll: (ids) => setSelected(new Set(ids)),
  } : null;
  // A status chip or the NDA toggle. The URL keeps the status (and All ∧ NDA);
  // the memory flag keeps NDA under any other status.
  const pick = (click) => {
    const next = kit.boardNext(filter, click);
    if (next.status !== (filter.status === 'all' ? null : filter.status)) api.set({ status: next.status });
    setNdaOnly(next.ndaOnly);
  };
  // The summary waits out the whole run (C0's hold): one refetch when it ends.
  const takeHold = () => {
    const r = window.holdRecordSummary ? window.holdRecordSummary('deal', dealId) : null;
    let done = false;
    const release = () => { if (done) return; done = true; holdRef.current = null; if (typeof r === 'function') r(); };
    holdRef.current = release;
    return release;
  };

  // Resolves true when it ran (or had nothing to change), false when the confirm was cancelled.
  const runBulk = async (action, opts) => {
    const plan = picked.map((b) => ({ b, patch: kit.bulkPatch(action, opts, b) }));
    const todo = plan.filter((x) => x.patch);
    const skipped = plan.length - todo.length;
    if (!todo.length) {
      window.pushToast({ type: 'info', title: kit.bulkDone(action, 0, plan.length), body: skipped ? `${skipped} skipped` : undefined });
      return true;
    }
    const ok = await window.showConfirm({
      title: kit.bulkTitle(action, todo.length),
      body: kit.bulkBody(action, skipped),
      confirmLabel: kit.bulkConfirmLabel(action), danger: action === 'pass',
    });
    if (!ok) return false;
    stopRef.current = false;
    const release = takeHold();
    setProgress({ done: 0, total: todo.length });
    const failed = [];
    let saved = 0;
    let stopped = false;
    try {
      for (const { b, patch } of todo) {                          // sequential: the existing per-row path, one at a time
        if (stopRef.current) { stopped = true; break; }
        try {
          const row = await window.AtlasApi.updateBuyerEngagement(b.id, patch);
          buyers.merge(b.id, row);
          saved += 1;
        } catch (e) {
          failed.push(b.buyer_name || '—');
        }
        setProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
      }
      if (saved) { boardWrote(); notify(); }                       // once, while still held: the release runs the one refetch
    } finally {
      release();
      setProgress(null);
      setSelected(new Set());
    }
    window.pushToast({
      type: failed.length ? 'error' : stopped ? 'info' : 'success',
      title: kit.bulkDone(action, saved, todo.length),
      body: [stopped ? 'Stopped' : null,
             failed.length ? `Failed: ${failed.slice(0, 3).join(', ')}${failed.length > 3 ? ` +${failed.length - 3}` : ''}` : null,
            ].filter(Boolean).join(' · ') || undefined,
    });
    return true;
  };

  const K = window.RecordNotesKit;
  const draftKey = K ? K.draftKeyFor('deal', dealId) : null;
  const logHere = (kind) => {
    if (!draftKey || !window.RecordDrafts) return;
    window.RecordComposerHost.claim('deal', dealId, 'workspace');
    window.RecordDrafts.set(draftKey, (p) => K.openDraft(p, { kind, today: window.localISODate(),
      taskSeed: kind === 'task' ? window.taskFormDraft({ activeUser: api.ctx.activeUser, initialLinks: K.dealTaskLinks(deal) }) : null }));
  };
  const closeDraft = () => { if (draftKey && window.RecordDrafts) window.RecordDrafts.set(draftKey, K.closeDraft); };
  const onComposerSaved = (r) => {
    if (r.type === 'task') { if (api.ctx.onTasksChange) api.ctx.onTasksChange(); window.dispatchEvent(new CustomEvent('task-created')); return; }
    if (api.ctx.reloadNotes) api.ctx.reloadNotes();
    if (r.updatesDeal && api.ctx.reloadRecord) api.ctx.reloadRecord();
  };
  const tabs = guest
    ? [{ key: 'board', label: 'Board', count: rows ? rows.length : null }]
    : [{ key: 'board', label: 'Board', count: rows ? rows.length : null },
       { key: 'outreach', label: 'Outreach', count: o && o.kpis ? (o.kpis.contacts || 0) : null }];
  const pane = { position: 'absolute', inset: 0, height: '100%', overflowY: 'auto' };
  return (
    <window.FullScreenPanel active={api.active} title={`${deal.name || 'Deal'} › Buyers`} maxWidth={1600} bodyScroll={false}
      tabs={tabs} tab={tab} onTab={(k) => api.set({ tab: k })}
      headerExtra={<><BuyersStagePill deal={deal} ctx={api.ctx} canWrite={canWrite} />{canWrite && window.LogButton && <window.LogButton onPick={logHere} dot={draftDot} />}</>}
      onClose={() => api.close({ via: 'button' })} onEscape={api.escape}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {!guest && (
          <BuyersSubHeader deal={deal} sum={sum} canWrite={canWrite}
            onAdd={() => setAdding(true)} onSearch={() => setSearching(true)} />
        )}
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <div ref={boardRef} data-buyers-pane="board" style={{ ...pane, display: tab === 'board' ? 'block' : 'none' }}>
            <div style={{ position: 'sticky', top: 0, zIndex: 3, background: 'var(--surface)', borderBottom: '1px solid var(--border)',
              padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <window.BeChip on={filter.axis === 'all'} label="All" count={counts.all} onClick={() => pick({ axis: 'all' })} />
              {kit.BOARD_CHIPS.map((c) => (
                <window.BeChip key={c.status} on={filter.axis === c.status} label={c.label} count={counts[c.status]}
                  onClick={() => pick({ axis: c.status })} />
              ))}
              <span style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 4px' }} />
              <window.BeChip on={filter.nda} label="NDA signed" count={counts.nda} onClick={() => pick({ nda: true })} />
              {canWrite && (
                <BuyersBulkBar n={picked.length} active={api.active && tab === 'board'} progress={progress}
                  onRun={runBulk} onClear={() => setSelected(new Set())} onStop={() => { stopRef.current = true; }} />
              )}
            </div>
            {rows == null
              ? (
                <div title={buyers.error || undefined}
                  style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, ...BW_DIM }}>
                  —{buyers.error && (
                    <window.Btn size="xs" variant="ghost" title="Retry" icon={<window.Icon.Refresh size={11} />} onClick={() => buyers.reload()} />
                  )}
                </div>
              )
              : (
                <window.BuyerBoardTable deal={deal} rows={shown} onPatch={canWrite ? boardPatch : null} onRowUpdate={onRowUpdate}
                  wide guest={guest} select={select} focusId={flash} empty={<span style={BW_DIM}>—</span>} />
              )}
          </div>
          {wantTracker && (
            <div data-buyers-pane="outreach" style={{ ...pane, display: tab === 'outreach' ? 'flex' : 'none', flexDirection: 'column' }}>
              <window.OutreachTracker deal={deal} company={(api.ctx && api.ctx.company) || null} embedded active={api.active}
                readOnly={!canWrite} tracker={trackerProp} onBack={null} />
            </div>
          )}
        </div>
      </div>
      {api.active && adding && window.AddBuyerToDealDrawer && (
        <window.AddBuyerToDealDrawer deal={{ id: deal.id, name: deal.name }} onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); buyers.reload(); boardWrote(); notify(); }} />
      )}
      {api.active && searching && window.BuyerSearchDrawer && (
        <window.BuyerSearchDrawer deal={deal} onClose={() => setSearching(false)}
          onAdded={() => { buyers.reload(); boardWrote(); notify(); }} />
      )}
      {canWrite && window.RecordComposer && (
        <WorkspaceComposerDrawer open={api.active && draftOpen && host === 'workspace'} onClose={closeDraft}>
          <window.RecordComposer rec={{ kind: 'deal', id: dealId }} attach={{ engagement_id: dealId, company_id: deal.target || null }} deal={deal}
            presentation="drawer" activeUser={api.ctx.activeUser} members={api.ctx.users} kinds={K ? K.composerKindsFor(true) : undefined} onSaved={onComposerSaved} />
        </WorkspaceComposerDrawer>
      )}
    </window.FullScreenPanel>
  );
};
window.BuyersWorkspace = BuyersWorkspace;
